import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers } from 'lucide-react';

import { Button, buttonClassName } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { sortByTeachingOrder } from '@/features/institute/classes/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import type { ClassInstallment } from '@/types/fees';
import type { InstituteClass } from '@/types/instituteClass';
import { feesApi } from '../api/fees.api';
import { ClassInstallmentsModal } from './ClassInstallmentsModal';

const CLASSES_PATH = '/admin/classes';

export interface ClassFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The page's class list — already loaded for the register's filters. */
  classes: InstituteClass[] | null;
  isLoading: boolean;
  error: ApiRequestError | null;
  onRetry: () => void;
  /** Whether the reader may change a class's fee, which happens on the Classes page. */
  canEdit: boolean;
  /** `FEE_MANAGE`: whether the reader may set a class's installments, here. */
  canManageInstallments: boolean;
}

const FEE_COLUMNS: Column<InstituteClass>[] = [
  {
    key: 'class',
    header: 'Class',
    render: (row) => <span className="font-semibold text-ink">{row.name}</span>,
  },
  {
    key: 'tuition',
    header: 'Tuition',
    align: 'right',
    render: (row) => <span className="tabular-nums">{formatCurrency(row.tuitionFee)}</span>,
  },
  {
    key: 'books',
    header: 'Books',
    align: 'right',
    hideOnMobile: true,
    render: (row) => <span className="tabular-nums">{formatCurrency(row.booksFee)}</span>,
  },
  {
    key: 'other',
    header: 'Other fees',
    align: 'right',
    hideOnMobile: true,
    render: (row) => <span className="tabular-nums">{formatCurrency(row.otherFees)}</span>,
  },
  {
    key: 'total',
    header: 'Total a year',
    align: 'right',
    render: (row) => <span className="font-semibold tabular-nums text-ink">{formatCurrency(row.totalFee)}</span>,
  },
];

/**
 * What each class is charged for the session, in one place, and the
 * installments it is collected in.
 *
 * The fee itself is read-only here: it is part of the class's setup, and it is
 * changed there so there is one form for it (CLAUDE.md §3). How that fee is
 * split into installments is a question for the fee office, so it is set here,
 * under `FEE_MANAGE`.
 */
export function ClassFeesModal({
  isOpen,
  onClose,
  classes,
  isLoading,
  error,
  onRetry,
  canEdit,
  canManageInstallments,
}: ClassFeesModalProps) {
  const rows = useMemo(() => sortByTeachingOrder(classes ?? [], 'LOWEST_FIRST'), [classes]);
  const [editing, setEditing] = useState<InstituteClass | null>(null);

  // Read only while the dialog is open; the fee screen has no other use for it.
  const schedules = useApiQuery(
    () => (isOpen ? feesApi.listInstallments() : Promise.resolve([])),
    [isOpen],
  );
  const byClass = useMemo(
    () => new Map((schedules.data ?? []).map((row) => [row.classUuid, row.installments])),
    [schedules.data],
  );
  const installmentsOf = (row: InstituteClass): ClassInstallment[] => byClass.get(row.uuid) ?? [];

  const columns: Column<InstituteClass>[] = [
    ...FEE_COLUMNS,
    {
      key: 'installments',
      header: 'Installments',
      className: 'min-w-[260px]',
      render: (row) => {
        const list = installmentsOf(row);
        return (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-ink-2 whitespace-nowrap">
              {schedules.isLoading
                ? '…'
                : list.length === 0
                  ? 'One amount for the year'
                  : `${list.length} · first due ${formatDate(list[0].dueDate)}`}
            </span>
            {canManageInstallments && (
              <Button
                variant="secondary"
                size="sm"
                className="whitespace-nowrap shrink-0"
                disabled={schedules.isLoading || Boolean(schedules.error)}
                onClick={() => setEditing(row)}
              >
                {list.length === 0 ? 'Set installments' : 'Change'}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Fee settings"
      description="What each class is charged this session. A concession is set on the student's own account."
      footer={
        canEdit ? (
          <Link
            to={CLASSES_PATH}
            onClick={() => {
              onClose();
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className={buttonClassName({ variant: 'secondary' })}
          >
            Change class's fee
          </Link>
        ) : undefined
      }
    >
      {error && !classes ? (
        <QueryError title="Could not load the class fees" error={error} onRetry={onRetry} />
      ) : (
        <Table
          columns={columns}
          rows={rows}
          rowKey={(row) => row.uuid}
          isLoading={isLoading}
          empty={
            <EmptyState
              icon={<Layers className="h-5 w-5" />}
              title="No classes set up yet"
              description="Add your classes and their fees on the Classes page, and they will be listed here."
            />
          }
        />
      )}

      {schedules.error && (
        <QueryError
          title="Could not load the installments"
          error={schedules.error}
          onRetry={schedules.refetch}
        />
      )}

      <ClassInstallmentsModal
        instituteClass={editing}
        installments={editing ? installmentsOf(editing) : []}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          schedules.refetch();
        }}
      />
    </Modal>
  );
}
